import { useState, useTransition } from 'react';

import { QueryErrorResetBoundary } from '@mint-lib/error-boundary';
import { useTranslation } from '@mint-lib/i18n';
import { Upload } from '@mint-lib/icons';
import {
  Button,
  Tabs,
  TabsList,
  TabsTrigger,
  typographyVariants,
} from '@mint-lib/tw-components';
import { cn } from '@mint-lib/tw-utils';

import { RichTooltip } from '../../components/atoms/RichTooltip.jsx';
import { CutFooterRowsAlert } from '../../components/molecules/CutFooterRowsAlert.jsx';
import { NavbarSlot } from '../../components/molecules/NavbarSlot.jsx';
import { FooterTab } from '../../components/organisms/FooterTab.jsx';
import { HeaderTab } from '../../components/organisms/HeaderTab.jsx';
import { SelectFormatsFields } from '../../components/organisms/SelectFormatsFields.jsx';
import { useDataFileContext } from '../../contexts/DataFileContext/useDataFileContext.js';
import { useDataFileFormats, useDataFilePreview } from '../../queries/index.js';

export const HeaderSelectionStep = () => {
  const { t } = useTranslation('@myn/import-hub');

  const { dataFileId, goToNextStep, isSubmitting, showUploadFileDialog } =
    useDataFileContext();

  const { data: dataFilePreview } = useDataFilePreview(dataFileId);

  const { data: dataFileFormats, isLoading: isHeadersFormatsLoading } =
    useDataFileFormats(dataFileId, {
      throwOnError: true,
    });

  const [, startTransition] = useTransition();
  const [activeTab, setActiveTab] = useState('header');
  const handleTabChange = (selectedTab: string) => {
    startTransition(() => {
      setActiveTab(selectedTab);
    });
  };

  const isUploading = dataFilePreview.status === 'uploading';
  const isValid = Boolean(dataFileFormats.dateOrder);

  return (
    <>
      <NavbarSlot>
        <div className="order-2 flex gap-2">
          <Button
            onClick={showUploadFileDialog}
            variant="text"
            disabled={isUploading}
            data-dd-action-name="Replace file"
          >
            <Upload />
            {t('Replace file')}
          </Button>
          <RichTooltip
            asChild={true}
            description={t('Select Date format (mandatory) to proceed.')}
            side="left"
            disabled={isHeadersFormatsLoading || isValid}
          >
            <div>
              <Button
                disabled={!isValid || isSubmitting}
                onClick={goToNextStep}
                data-dd-action-name="Next"
              >
                {t('Next')}
              </Button>
            </div>
          </RichTooltip>
        </div>
      </NavbarSlot>
      <div className="mb-2 flex items-end justify-between gap-4">
        <div>
          <h4
            className={cn(
              typographyVariants({ variant: 'h4', color: 'primary' }),
              'mb-2',
            )}
          >
            {t('File import setup')}
          </h4>
          <p
            className={typographyVariants({
              variant: 'bodyLong01',
              color: 'primary',
            })}
          >
            {t(
              'Set the file header, remove unwanted footer rows, and define date and number formats for your imported file.',
            )}
          </p>
        </div>
      </div>

      {Boolean(dataFilePreview.footerRowsCount) && <CutFooterRowsAlert />}

      <div className="mb-4 flex items-end">
        <Tabs
          value={activeTab}
          onValueChange={handleTabChange}
          orientation="horizontal"
          className="w-full"
        >
          <TabsList className="w-full">
            <TabsTrigger value="header" data-dd-action-name="Header tab">
              {t('Header')}
            </TabsTrigger>{' '}
            <TabsTrigger value="footer" data-dd-action-name="Footer tab">
              {t('Footer')}
            </TabsTrigger>
          </TabsList>
        </Tabs>
        <SelectFormatsFields
          dataFileId={dataFileId}
          values={dataFileFormats}
          isValuesLoading={isHeadersFormatsLoading}
        />
      </div>

      <QueryErrorResetBoundary>
        {activeTab === 'header' && <HeaderTab dataFileId={dataFileId} />}
        {activeTab === 'footer' && (
          <FooterTab
            dataFileId={dataFileId}
            dataFilePreview={dataFilePreview}
          />
        )}
      </QueryErrorResetBoundary>
    </>
  );
};
